import type {
  Brand,
  BrandId,
  ChatMessage,
  ClosedDeal,
  LeadershipContact,
  Loan,
  Milestone,
  TeamMember,
} from '../types/portal'

// All data below is fictional sample data for prototype purposes.

export const brands: Record<BrandId, Brand> = {
  nmhl: {
    id: 'nmhl',
    name: 'National Mortgage Home Loans',
    mark: 'N',
    primary: '#C8102E',
    primaryDark: '#A30D25',
    soft: '#FCEDEF',
    ink: '#16203A',
    legal: 'National Mortgage Home Loans · NMLS #000000 · Equal Housing Opportunity',
  },
}

export const milestones: Milestone[] = [
  'Pre-Approved',
  'Application',
  'Appraisal',
  'Underwriting',
  'Conditional Approval',
  'Clear to Close',
  'Closed',
]

export const realtor = {
  name: 'Sam Whitaker',
  company: 'Whitaker Realty Group',
  initials: 'SW',
}

const alex: TeamMember = { role: 'Loan officer', name: 'Alex Moreno', phone: '(734) 555-0142', email: 'alex.moreno@nmhl.example' }
const renee: TeamMember = { role: 'Loan officer', name: 'Renee Lambert', phone: '(734) 555-0163', email: 'renee.lambert@nmhl.example' }
const jamie: TeamMember = { role: 'Processor', name: 'Jamie Ortiz', phone: '(734) 555-0187', email: 'jamie.ortiz@nmhl.example' }
const riley: TeamMember = { role: 'Processor', name: 'Riley Park', phone: '(734) 555-0174', email: 'riley.park@nmhl.example' }
const taylor: TeamMember = { role: 'Closer', name: 'Taylor Brooks', phone: '(734) 555-0119', email: 'taylor.brooks@nmhl.example' }

export const loanOfficer = {
  ...alex,
  title: 'Senior Loan Officer',
  nmls: 'NMLS #000000',
  initials: 'AM',
  branch: 'Wyandotte Branch',
}

export const loanOfficerOptions = ['Alex Moreno', 'Renee Lambert', 'Chris Dunham']

export const loans: Loan[] = [
  {
    id: 'rivera',
    buyer: 'Jordan Rivera',
    lastName: 'Rivera',
    street: '214 Maple Ct',
    streetKey: 'maple',
    city: 'Wyandotte, MI',
    status: 'in-process',
    milestone: 'Underwriting',
    milestoneDates: { 'Pre-Approved': 'Aug 28', Application: 'Sep 22', Appraisal: 'Oct 18', Underwriting: 'Oct 19' },
    estClose: 'Nov 6, 2026',
    estCloseShort: 'Nov 6',
    contractClose: 'Nov 6, 2026',
    appraisalOrdered: 'Oct 9, 2026',
    appraisalReceived: 'Oct 18, 2026',
    preApprovalExpires: 'Nov 26, 2026',
    loanOfficer: 'Alex Moreno',
    lastUpdate: 'Oct 21',
    team: [alex, jamie, taylor],
    updates: [
      { date: 'Oct 21', text: 'Underwriter review in progress. Closing estimate confirmed for Nov 6.' },
      { date: 'Oct 19', text: 'File submitted to underwriting.' },
      { date: 'Oct 18', text: 'Appraisal received.' },
      { date: 'Oct 9', text: 'Appraisal ordered.' },
      { date: 'Sep 22', text: 'Application completed.' },
    ],
  },
  {
    id: 'chen',
    buyer: 'Priya Chen',
    lastName: 'Chen',
    street: '88 Riverside Dr',
    streetKey: 'riverside',
    city: 'Trenton, MI',
    status: 'in-process',
    milestone: 'Clear to Close',
    milestoneDates: {
      'Pre-Approved': 'Jul 30',
      Application: 'Aug 21',
      Appraisal: 'Sep 12',
      Underwriting: 'Sep 16',
      'Conditional Approval': 'Oct 3',
      'Clear to Close': 'Oct 20',
    },
    estClose: 'Oct 30, 2026',
    estCloseShort: 'Oct 30',
    contractClose: 'Oct 30, 2026',
    appraisalOrdered: 'Sep 2, 2026',
    appraisalReceived: 'Sep 12, 2026',
    preApprovalExpires: 'Oct 28, 2026',
    loanOfficer: 'Alex Moreno',
    lastUpdate: 'Oct 20',
    team: [alex, riley, taylor],
    updates: [
      { date: 'Oct 20', text: 'Clear to close issued. Closing scheduled for Oct 30.' },
      { date: 'Oct 3', text: 'Conditional approval issued.' },
      { date: 'Sep 16', text: 'File submitted to underwriting.' },
      { date: 'Sep 12', text: 'Appraisal received.' },
    ],
  },
  {
    id: 'bell',
    buyer: 'Marcus Bell',
    lastName: 'Bell',
    street: '1502 Oak St',
    streetKey: 'oak',
    city: 'Southgate, MI',
    status: 'in-process',
    milestone: 'Appraisal',
    milestoneDates: { 'Pre-Approved': 'Sep 4', Application: 'Oct 6', Appraisal: 'Oct 14' },
    estClose: 'Nov 20, 2026',
    estCloseShort: 'Nov 20',
    contractClose: 'Nov 20, 2026',
    appraisalOrdered: 'Oct 14, 2026',
    preApprovalExpires: 'Dec 3, 2026',
    loanOfficer: 'Alex Moreno',
    lastUpdate: 'Oct 17',
    team: [alex, jamie, taylor],
    updates: [
      { date: 'Oct 17', text: 'Appraisal inspection scheduled for Oct 23.' },
      { date: 'Oct 14', text: 'Appraisal ordered.' },
      { date: 'Oct 6', text: 'Application completed.' },
    ],
  },
  {
    id: 'kowalski',
    buyer: 'Dana Kowalski',
    lastName: 'Kowalski',
    street: '37 Elm Ave',
    streetKey: 'elm',
    city: 'Grosse Ile, MI',
    status: 'in-process',
    milestone: 'Application',
    milestoneDates: { 'Pre-Approved': 'Sep 30', Application: 'Oct 16' },
    estClose: 'Dec 4, 2026',
    estCloseShort: 'Dec 4',
    contractClose: 'Dec 4, 2026',
    preApprovalExpires: 'Dec 29, 2026',
    loanOfficer: 'Renee Lambert',
    lastUpdate: 'Oct 16',
    team: [renee, riley, taylor],
    updates: [
      { date: 'Oct 16', text: 'Application started. Purchase contract received.' },
      { date: 'Sep 30', text: 'Buyer pre-approved.' },
    ],
  },
  {
    id: 'osei',
    buyer: 'Kevin Osei',
    lastName: 'Osei',
    street: '410 Front St',
    streetKey: 'front',
    city: 'Wyandotte, MI',
    status: 'closed',
    milestone: 'Closed',
    milestoneDates: {
      'Pre-Approved': 'Jul 2',
      Application: 'Aug 1',
      Appraisal: 'Aug 22',
      Underwriting: 'Aug 26',
      'Conditional Approval': 'Sep 10',
      'Clear to Close': 'Oct 2',
      Closed: 'Oct 9',
    },
    estClose: 'Oct 9, 2026',
    estCloseShort: 'Oct 9',
    contractClose: 'Oct 9, 2026',
    appraisalOrdered: 'Aug 12, 2026',
    appraisalReceived: 'Aug 22, 2026',
    preApprovalExpires: 'Oct 1, 2026',
    loanOfficer: 'Alex Moreno',
    lastUpdate: 'Oct 9',
    team: [alex, jamie, taylor],
    updates: [
      { date: 'Oct 9', text: 'Loan closed. Congratulations to the buyer!' },
      { date: 'Oct 2', text: 'Clear to close issued.' },
    ],
  },
  {
    id: 'hart',
    buyer: 'Elena Hart',
    lastName: 'Hart',
    street: '9 Birch Ln',
    streetKey: 'birch',
    city: 'Riverview, MI',
    status: 'closed',
    milestone: 'Closed',
    milestoneDates: {
      'Pre-Approved': 'Jun 18',
      Application: 'Jul 20',
      Appraisal: 'Aug 8',
      Underwriting: 'Aug 11',
      'Conditional Approval': 'Aug 29',
      'Clear to Close': 'Sep 19',
      Closed: 'Sep 26',
    },
    estClose: 'Sep 26, 2026',
    estCloseShort: 'Sep 26',
    contractClose: 'Sep 26, 2026',
    appraisalOrdered: 'Jul 29, 2026',
    appraisalReceived: 'Aug 8, 2026',
    preApprovalExpires: 'Sep 16, 2026',
    loanOfficer: 'Alex Moreno',
    lastUpdate: 'Sep 26',
    team: [alex, riley, taylor],
    updates: [{ date: 'Sep 26', text: 'Loan closed.' }],
  },
  {
    id: 'mitchell',
    buyer: 'Lauren Mitchell',
    lastName: 'Mitchell',
    street: '2215 Fort St',
    streetKey: 'fort',
    city: 'Lincoln Park, MI',
    status: 'closed',
    milestone: 'Closed',
    milestoneDates: {
      'Pre-Approved': 'Apr 22',
      Application: 'May 19',
      Appraisal: 'Jun 6',
      Underwriting: 'Jun 9',
      'Conditional Approval': 'Jun 24',
      'Clear to Close': 'Jul 11',
      Closed: 'Jul 18',
    },
    estClose: 'Jul 18, 2026',
    estCloseShort: 'Jul 18',
    contractClose: 'Jul 18, 2026',
    appraisalOrdered: 'May 28, 2026',
    appraisalReceived: 'Jun 6, 2026',
    preApprovalExpires: 'Jul 21, 2026',
    loanOfficer: 'Renee Lambert',
    lastUpdate: 'Jul 18',
    team: [renee, jamie, taylor],
    updates: [{ date: 'Jul 18', text: 'Loan closed.' }],
  },
]

export const closedDealsYtd: ClosedDeal[] = [
  { buyer: 'Kevin Osei', city: 'Wyandotte, MI', closed: 'Oct 9, 2026' },
  { buyer: 'Elena Hart', city: 'Riverview, MI', closed: 'Sep 26, 2026' },
  { buyer: 'Lauren Mitchell', city: 'Lincoln Park, MI', closed: 'Jul 18, 2026' },
  { buyer: 'Andre Williams', city: 'Taylor, MI', closed: 'Jun 27, 2026' },
  { buyer: 'Sofia Novak', city: 'Allen Park, MI', closed: 'Jun 6, 2026' },
  { buyer: 'Kate Doyle', city: 'Trenton, MI', closed: 'May 15, 2026' },
  { buyer: 'Hassan Farah', city: 'Dearborn, MI', closed: 'Apr 30, 2026' },
  { buyer: 'Megan Torres', city: 'Southgate, MI', closed: 'Apr 11, 2026' },
  { buyer: 'Chris Lindqvist', city: 'Grosse Ile, MI', closed: 'Mar 20, 2026' },
  { buyer: 'Ava Patel', city: 'Wyandotte, MI', closed: 'Feb 27, 2026' },
  { buyer: 'Nate Kim', city: 'Riverview, MI', closed: 'Jan 23, 2026' },
]

export const leadership: LeadershipContact[] = [
  { name: 'Diane Foster', title: 'Branch Manager', phone: '(734) 555-0100', email: 'diane.foster@nmhl.example' },
  { name: 'Greg Halloran', title: 'Regional Sales Director', phone: '(734) 555-0108', email: 'greg.halloran@nmhl.example' },
  { name: 'Monica Reyes', title: 'Client Experience Lead', phone: '(734) 555-0131', email: 'monica.reyes@nmhl.example' },
]

export const chatSuggestions = [
  'When is the Rivera closing?',
  'Is the appraisal back on 214 Maple?',
  'What’s left to clear to close?',
  'Who is the processor on the Chen file?',
]

export const seedMessages: ChatMessage[] = [
  { id: 'seed-q', role: 'user', text: 'Is the appraisal back on 214 Maple Ct?' },
  {
    id: 'seed-a',
    role: 'assistant',
    text: 'Yes. The appraisal for Jordan Rivera at 214 Maple Ct, Wyandotte MI was received Oct 18. The file is in underwriting and estimated closing is still Nov 6.',
    source: 'Loan milestones · updated Oct 21',
  },
]

export const timelineOptions = ['Under contract now', 'Within 30 days', '1–3 months', '3–6 months', 'Just exploring']
export const priceRangeOptions = ['Under $200k', '$200k–$300k', '$300k–$450k', '$450k–$600k', '$600k+']
